import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getInventories, getStock, getCategories, getBrands, transfer } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { 
  ArrowRightLeft, 
  Trash2, 
  Loader2, 
  Package,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Search,
  ChevronDown,
  Check,
  X
} from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import { ItemThumbnail } from '../components/ImageUploader';

const Transfer = () => {
  const navigate = useNavigate();
  
  const [inventories, setInventories] = useState([]);
  const [sourceStock, setSourceStock] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingStock, setLoadingStock] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  const [sourceInventory, setSourceInventory] = useState('');
  const [destinationInventory, setDestinationInventory] = useState('');
  const [lineItems, setLineItems] = useState([]);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterSubCategory, setFilterSubCategory] = useState('all');
  const [filterBrand, setFilterBrand] = useState('all');
  const [catOpen, setCatOpen] = useState(false);
  const [subCatOpen, setSubCatOpen] = useState(false);
  const [brandOpen, setBrandOpen] = useState(false);

  useEffect(() => {
    const fetchInventories = async () => {
      try {
        const [invRes, catRes, brandRes] = await Promise.all([
          getInventories(), getCategories(), getBrands()
        ]);
        setInventories(invRes.data);
        setCategories(catRes.data);
        setBrands(brandRes.data);
        if (invRes.data.length >= 2) {
          setSourceInventory(invRes.data[0].id);
          setDestinationInventory(invRes.data[1].id);
        }
      } catch (err) {
        toast.error('Failed to load inventories');
      } finally {
        setLoading(false);
      }
    };
    fetchInventories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { setFilterSubCategory('all'); }, [filterCategory]);

  useEffect(() => {
    if (sourceInventory) {
      fetchSourceStock();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceInventory]);

  const fetchSourceStock = async () => {
    setLoadingStock(true);
    try {
      const stockRes = await getStock({ inventory_id: sourceInventory });
      setSourceStock(stockRes.data.filter(s => s.quantity > 0));
    } catch (err) {
      toast.error('Failed to load stock');
    } finally {
      setLoadingStock(false);
    }
  };

  const availableDestinations = inventories.filter(inv => inv.id !== sourceInventory);

  const parentCategories = useMemo(() => categories.filter(c => !c.parent_id), [categories]);
  const getSubcategories = (parentId) => categories.filter(c => c.parent_id === parentId);
  const filterSubcategories = useMemo(
    () => filterCategory !== 'all' ? getSubcategories(filterCategory) : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterCategory, categories]
  );

  const filteredStock = useMemo(() => sourceStock.filter(item => {
    if (filterCategory !== 'all') {
      const childIds = getSubcategories(filterCategory).map(c => c.id);
      if (!childIds.includes(item.category_id)) return false;
    }
    if (filterSubCategory !== 'all' && item.category_id !== filterSubCategory) return false;
    if (filterBrand !== 'all' && item.brand_id !== filterBrand) return false;
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return item.item_name.toLowerCase().includes(q) ||
      item.internal_code.toLowerCase().includes(q) ||
      (item.brand_name || '').toLowerCase().includes(q) ||
      (item.category_name || '').toLowerCase().includes(q) ||
      (item.vendor_skus || []).some(sku => sku.toLowerCase().includes(q));
  }), [sourceStock, filterCategory, filterSubCategory, filterBrand, searchQuery, categories]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearFilters = () => {
    setFilterCategory('all');
    setFilterSubCategory('all');
    setFilterBrand('all');
    setSearchQuery('');
  };

  const addItem = (stockItem) => {
    if (stockItem.quantity <= 0) { toast.error('No stock available'); return; }
    const existing = lineItems.find(li => li.item_id === stockItem.item_id);
    if (existing) {
      if (existing.quantity + 1 > stockItem.quantity) {
        toast.warning(`Only ${stockItem.quantity} available.`);
        return;
      }
      setLineItems(prev => prev.map(li =>
        li.item_id === stockItem.item_id ? { ...li, quantity: li.quantity + 1 } : li
      ));
    } else {
      setLineItems(prev => [...prev, {
        item_id: stockItem.item_id,
        item_name: stockItem.item_name,
        internal_code: stockItem.internal_code,
        image_ids: stockItem.image_ids || [],
        quantity: 1,
        available: stockItem.quantity,
        unit: stockItem.unit
      }]);
    }
    setError('');
  };

  const updateQuantity = (itemId, qty) => {
    const n = Math.max(1, parseInt(qty) || 1);
    setLineItems(prev => prev.map(li => {
      if (li.item_id === itemId) {
        if (n > li.available) {
          toast.warning(`Only ${li.available} ${li.unit} available.`);
          return { ...li, quantity: li.available };
        }
        return { ...li, quantity: n };
      }
      return li;
    }));
  };

  const removeLineItem = (itemId) => {
    setLineItems(prev => prev.filter(li => li.item_id !== itemId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (sourceInventory === destinationInventory) {
      setError('Source and destination must be different');
      return;
    }
    
    if (lineItems.length === 0) {
      setError('Please add at least one item to transfer');
      return;
    }
    
    setSubmitting(true);
    setError('');
    
    try {
      await transfer({
        source_inventory_id: sourceInventory,
        destination_inventory_id: destinationInventory,
        line_items: lineItems.map(li => ({
          item_id: li.item_id,
          quantity: li.quantity
        }))
      });
      setSuccess(true);
      setTimeout(() => {
        navigate('/inventory');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to complete transfer');
    } finally {
      setSubmitting(false);
    }
  };

  const getInventoryName = (id) => inventories.find(i => i.id === id)?.inventory_name || 'Unknown';

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-[#0776BD]" />
      </div>
    );
  }

  if (success) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] animate-slide-in">
        <div className="bg-green-100 p-6 rounded-full mb-4">
          <CheckCircle size={64} className="text-green-600" />
        </div>
        <h2 className="heading-2 text-green-600 mb-2">Transfer Complete!</h2>
        <p className="text-[#6B7280]">Redirecting to inventory...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="transfer-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="hover:bg-[#E6F2FA]" data-testid="back-btn">
          <ArrowLeft size={20} className="text-[#0776BD]" />
        </Button>
        <div>
          <h1 className="heading-2 text-[#1F2937]">Transfer Stock</h1>
          <p className="text-[#6B7280]">Move items between inventories</p>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="rounded-lg">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        {/* Source/Destination Selection */}
        <Card className="bg-white rounded-xl border border-gray-200 shadow-soft mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center gap-4">
              <div className="flex-1 w-full">
                <Label className="text-sm font-medium text-[#1F2937] mb-2 block">From</Label>
                <Select value={sourceInventory} onValueChange={setSourceInventory}>
                  <SelectTrigger className="h-14 rounded-lg border-gray-300 text-lg" data-testid="source-inventory-select">
                    <SelectValue placeholder="Select source" />
                  </SelectTrigger>
                  <SelectContent>
                    {inventories.map(inv => (
                      <SelectItem key={inv.id} value={inv.id}>{inv.inventory_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="p-4 bg-[#0776BD] rounded-full shadow-md">
                <ArrowRight size={24} className="text-white" />
              </div>
              
              <div className="flex-1 w-full">
                <Label className="text-sm font-medium text-[#1F2937] mb-2 block">To</Label>
                <Select value={destinationInventory} onValueChange={setDestinationInventory}>
                  <SelectTrigger className="h-14 rounded-lg border-gray-300 text-lg" data-testid="destination-inventory-select">
                    <SelectValue placeholder="Select destination" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableDestinations.map(inv => (
                      <SelectItem key={inv.id} value={inv.id}>{inv.inventory_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Items to Transfer */}
        <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-[#1F2937] flex items-center gap-2">
              <ArrowRightLeft size={20} className="text-[#0776BD]" />
              Items to Transfer
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Filter row */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Popover open={catOpen} onOpenChange={setCatOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="h-10 justify-between rounded-lg border-gray-300 font-normal text-sm w-full" data-testid="transfer-category-filter">
                    <span className="truncate">{filterCategory !== 'all' ? parentCategories.find(c => c.id === filterCategory)?.category_name || 'Category' : 'Category'}</span>
                    <ChevronDown className="ml-1 h-4 w-4 shrink-0 opacity-60" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search categories..." />
                    <CommandList className="max-h-[220px]">
                      <CommandEmpty>No results found</CommandEmpty>
                      <CommandGroup>
                        <CommandItem onSelect={() => { setFilterCategory('all'); setCatOpen(false); }}>
                          <Check className={`mr-2 h-4 w-4 text-[#0776BD] ${filterCategory === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                          All Categories
                        </CommandItem>
                        {parentCategories.map(cat => (
                          <CommandItem key={cat.id} value={cat.category_name} onSelect={() => { setFilterCategory(cat.id); setCatOpen(false); }}>
                            <Check className={`mr-2 h-4 w-4 text-[#0776BD] ${filterCategory === cat.id ? 'opacity-100' : 'opacity-0'}`} />
                            {cat.category_name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              <Popover open={subCatOpen} onOpenChange={setSubCatOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" disabled={filterCategory === 'all'} className="h-10 justify-between rounded-lg border-gray-300 font-normal text-sm w-full" data-testid="transfer-subcategory-filter">
                    <span className="truncate">{filterSubCategory !== 'all' ? filterSubcategories.find(c => c.id === filterSubCategory)?.category_name || 'Subcategory' : 'Subcategory'}</span>
                    <ChevronDown className="ml-1 h-4 w-4 shrink-0 opacity-60" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search subcategories..." />
                    <CommandList className="max-h-[220px]">
                      <CommandEmpty>No results found</CommandEmpty>
                      <CommandGroup>
                        <CommandItem onSelect={() => { setFilterSubCategory('all'); setSubCatOpen(false); }}>
                          <Check className={`mr-2 h-4 w-4 text-[#0776BD] ${filterSubCategory === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                          All Subcategories
                        </CommandItem>
                        {filterSubcategories.map(cat => (
                          <CommandItem key={cat.id} value={cat.category_name} onSelect={() => { setFilterSubCategory(cat.id); setSubCatOpen(false); }}>
                            <Check className={`mr-2 h-4 w-4 text-[#0776BD] ${filterSubCategory === cat.id ? 'opacity-100' : 'opacity-0'}`} />
                            {cat.category_name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              <Popover open={brandOpen} onOpenChange={setBrandOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="h-10 justify-between rounded-lg border-gray-300 font-normal text-sm w-full" data-testid="transfer-brand-filter">
                    <span className="truncate">{filterBrand !== 'all' ? brands.find(b => b.id === filterBrand)?.brand_name || 'Brand' : 'Brand'}</span>
                    <ChevronDown className="ml-1 h-4 w-4 shrink-0 opacity-60" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search brands..." />
                    <CommandList className="max-h-[220px]">
                      <CommandEmpty>No results found</CommandEmpty>
                      <CommandGroup>
                        <CommandItem onSelect={() => { setFilterBrand('all'); setBrandOpen(false); }}>
                          <Check className={`mr-2 h-4 w-4 text-[#0776BD] ${filterBrand === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                          All Brands
                        </CommandItem>
                        {brands.map(b => (
                          <CommandItem key={b.id} value={b.brand_name} onSelect={() => { setFilterBrand(b.id); setBrandOpen(false); }}>
                            <Check className={`mr-2 h-4 w-4 text-[#0776BD] ${filterBrand === b.id ? 'opacity-100' : 'opacity-0'}`} />
                            {b.brand_name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Search row */}
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" size={18} />
                <Input
                  placeholder="Search by item name, code, or vendor SKU..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-10 rounded-lg border-gray-300 focus:border-[#0776BD]"
                  data-testid="transfer-search-input"
                />
              </div>
              {(filterCategory !== 'all' || filterSubCategory !== 'all' || filterBrand !== 'all' || searchQuery) && (
                <Button type="button" variant="ghost" size="sm" onClick={clearFilters} className="h-10 text-xs text-[#6B7280] hover:text-red-500" data-testid="transfer-clear-filters-btn">
                  <X size={14} className="mr-1" /> Clear filters
                </Button>
              )}
            </div>

            {/* Item list - 1 column */}
            {loadingStock ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-5 w-5 animate-spin text-[#0776BD]" />
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-2 max-h-[420px] overflow-y-auto pr-1">
                {filteredStock.map((item) => {
                  const added = lineItems.find(li => li.item_id === item.item_id);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => addItem(item)}
                      disabled={item.quantity === 0}
                      data-testid={`transfer-stock-item-${item.id}`}
                      className={`p-3 border rounded-lg text-left transition-all flex items-center gap-3 ${
                        added ? 'border-[#0776BD] bg-[#E6F2FA]' :
                        item.quantity === 0 ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed' :
                        'border-gray-200 hover:border-[#0776BD] hover:bg-[#E6F2FA]/50'
                      }`}
                    >
                      <ItemThumbnail imageIds={item.image_ids} size={40} alt={item.item_name} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#1F2937] truncate text-sm">{item.item_name}</p>
                        <p className="text-xs text-[#6B7280] truncate">{item.brand_name}{item.brand_name && item.category_name ? ' · ' : ''}{item.category_name}</p>
                        <p className="text-xs text-[#0776BD] font-mono">{item.internal_code}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-bold text-[#1F2937]">{item.quantity}</p>
                        <p className="text-[10px] text-[#6B7280]">{item.unit}</p>
                      </div>
                    </button>
                  );
                })}
                {filteredStock.length === 0 && (
                  <div className="py-10 text-center text-[#6B7280] border-2 border-dashed border-gray-300 rounded-lg">
                    No items match your filters
                  </div>
                )}
              </div>
            )}

            {/* Line Items Table */}
            {lineItems.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F5F6F7]">
                    <TableHead className="font-semibold text-[#1F2937]">Item</TableHead>
                    <TableHead className="font-semibold text-[#1F2937] text-center w-28">Quantity</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lineItems.map((li) => (
                    <TableRow key={li.item_id} data-testid={`transfer-line-${li.item_id}`}>
                      <TableCell className="py-2">
                        <div className="flex items-center gap-2">
                          <ItemThumbnail imageIds={li.image_ids} size={32} alt={li.item_name} />
                          <div className="min-w-0">
                            <p className="font-medium text-[#1F2937] text-sm truncate">{li.item_name}</p>
                            <p className="text-xs text-[#6B7280] font-mono">{li.internal_code}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="py-2 text-center">
                        <Input
                          type="number"
                          min="1"
                          max={li.available}
                          value={li.quantity}
                          onChange={(e) => updateQuantity(li.item_id, e.target.value)}
                          className="h-8 w-16 mx-auto rounded-md border-gray-300 text-center text-sm"
                          data-testid={`transfer-qty-${li.item_id}`}
                        />
                        <p className="text-[10px] text-[#6B7280] mt-0.5">of {li.available}</p>
                      </TableCell>
                      <TableCell className="py-2 text-right">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeLineItem(li.item_id)}
                          className="h-8 w-8 text-red-500 hover:bg-red-50"
                          data-testid={`remove-transfer-line-${li.item_id}`}
                          aria-label="Remove item"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="py-8 text-center text-[#6B7280] border-2 border-dashed border-gray-300 rounded-lg">
                <Package size={32} className="mx-auto mb-2 opacity-50" />
                <p>No items selected for transfer</p>
                <p className="text-sm">Pick items from the list above</p>
              </div>
            )}

            {/* Summary and Submit */}
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-4 border-t border-gray-200">
              <div className="text-center md:text-left">
                <p className="text-sm text-[#6B7280]">
                  Transferring <span className="font-bold text-[#1F2937]">{lineItems.length}</span> item(s) from{' '}
                  <span className="font-medium text-[#0776BD]">{getInventoryName(sourceInventory)}</span> to{' '}
                  <span className="font-medium text-[#0776BD]">{getInventoryName(destinationInventory)}</span>
                </p>
              </div>
              <Button 
                type="submit" 
                disabled={submitting || lineItems.length === 0}
                className="bg-[#0776BD] hover:bg-[#055A92] px-8 rounded-lg"
                data-testid="submit-transfer-btn"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ArrowRightLeft size={20} className="mr-2" />}
                Complete Transfer
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
};

export default Transfer;
